// - - - - - - - - - -
// jobFilters — what the filter boxes hide (#704, Leland 2026-11-02: "fix all of 704").
//
// ONE source of truth per filter group: the box list in keys.js (FILTERS_*_ARRAY). A box's
// position is where a user's saved Users.defaultFilters.<group>[] is read, and its `match`
// is the values it controls. Before #704 the surface rule used a separate set of index
// constants that disagreed with the boxes from position 6 on — "Internal" hid UNK jobs, the
// "UNK" box hid StormDoor (none exist), and Window jobs were always hidden.
//
// Rules:
//   - A saved array shorter than today's box list reads the missing boxes as TICKED (shown).
//     95 PROD users carried a 7-item surfaceFilters (the old default), whose missing
//     positions used to read as off.
//   - Values compare trimmed and case-insensitively ("Carpet " is Carpet). A job with a blank
//     surface counts as UNK.
//   - A value no box lists is never hidden.
// - - - - - - - - - -
import {
  FILTERS_SURFACE_ARRAY,
  FILTERS_CLASSIFICATION_ARRAY,
  FILTERS_CREW_ARRAY,
  FILTERS_CREW_SURFACE_ARRAY,
} from "./keys";

const GROUPS = {
  surfaceFilters: FILTERS_SURFACE_ARRAY,
  classificationFilters: FILTERS_CLASSIFICATION_ARRAY,
  crewFilters: FILTERS_CREW_ARRAY,
  crewSurfaceFilters: FILTERS_CREW_SURFACE_ARRAY,
};

const norm = (v) => String(v ?? "").trim().toLowerCase();

/** A saved filter array at the box list's length: missing / non-boolean entries → ticked. */
export function normalizeFilterArray(saved, boxes) {
  const src = Array.isArray(saved) ? saved : [];
  return boxes.map((_, i) => (typeof src[i] === "boolean" ? src[i] : true));
}

/** All four groups at today's lengths. Other keys on the saved object are kept. */
export function normalizeFilters(saved) {
  const src = saved && typeof saved === "object" ? saved : {};
  const out = { ...src };
  for (const [key, boxes] of Object.entries(GROUPS)) out[key] = normalizeFilterArray(src[key], boxes);
  return out;
}

/** The normalized values the UNTICKED boxes of one group control. Empty set = nothing hidden. */
export function hiddenValues(saved, boxes) {
  const ticked = normalizeFilterArray(saved, boxes);
  const out = new Set();
  boxes.forEach((box, i) => {
    if (!ticked[i]) for (const m of box.match || []) out.add(norm(m));
  });
  return out;
}

/** True when the job passes the user's surface and classification boxes. */
export function isJobShownByFilters(doc, filters) {
  const hiddenSurfaces = hiddenValues(filters?.surfaceFilters, FILTERS_SURFACE_ARRAY);
  const hiddenClasses = hiddenValues(filters?.classificationFilters, FILTERS_CLASSIFICATION_ARRAY);
  return !hiddenSurfaces.has(norm(doc?.surface)) && !hiddenClasses.has(norm(doc?.classification));
}

/** The jobs the user's boxes show. Same array back when nothing is hidden. */
export function applyJobFilters(docs, filters) {
  if (!Array.isArray(docs)) return docs;
  const hiddenSurfaces = hiddenValues(filters?.surfaceFilters, FILTERS_SURFACE_ARRAY);
  const hiddenClasses = hiddenValues(filters?.classificationFilters, FILTERS_CLASSIFICATION_ARRAY);
  if (hiddenSurfaces.size === 0 && hiddenClasses.size === 0) return docs;
  const out = docs.filter(
    (doc) => !hiddenSurfaces.has(norm(doc?.surface)) && !hiddenClasses.has(norm(doc?.classification))
  );
  return out.length === docs.length ? docs : out;
}

/** The types on a crew record (Recruiter separates them with returns), normalized. */
export function crewTypes(candidate) {
  return String(candidate?.RecordType ?? "")
    .split(/[\r\n,]+/)
    .map(norm)
    .filter(Boolean);
}

/**
 * True when the crew passes the user's crew-type and crew-surface boxes.
 * Type: hidden only when it has at least one type a box controls and EVERY such type is
 * unticked (an "Installer + Employee" crew stays while either box is ticked; unknown types
 * are ignored). Surface: hidden when its Scheduling_Surface box is unticked; blank → kept.
 */
export function isCrewShownByFilters(candidate, filters) {
  const boxTypes = new Set(FILTERS_CREW_ARRAY.flatMap((b) => b.match.map(norm)));
  const hiddenTypes = hiddenValues(filters?.crewFilters, FILTERS_CREW_ARRAY);
  const known = crewTypes(candidate).filter((t) => boxTypes.has(t));
  if (known.length > 0 && known.every((t) => hiddenTypes.has(t))) return false;
  const hiddenSurfaces = hiddenValues(filters?.crewSurfaceFilters, FILTERS_CREW_SURFACE_ARRAY);
  return !hiddenSurfaces.has(norm(candidate?.Scheduling_Surface));
}
